import {
	FRAME, artRuns, boneCrossing, cutPolylines, frameRect, insetCell, mulberry32, raggedPolygon, splitCells,
} from './goreGeometry.js';
import {
	PALETTE, blotches, boneWidth, brokenBone, coverage, fibres, guts, lumps, ribs, skullEdge, speckles, stains,
} from './goreShapes.js';

export { PALETTE };

const TEAR = 2.2;
const TEAR_SPACING = 2.6;
const RIM = 9;
const WAVE = 3.4;
const WAVE_SPACING = 3.2;
const MIN_COVERAGE = 40;
const CUT_RIM = TEAR + 1.5;

const limbBone = (shapes, art, bone, cell, random, rim) => {
	const poly = cell.poly;
	for (let i = 0, n = poly.length; i < n; i++) {
		const a = poly[i];
		if (a.cut === FRAME) continue;
		const b = poly[(i + 1) % n];
		const hit = boneCrossing(bone, a, b);
		if (!hit || art.alphaAt(hit.x, hit.y) < 0.9) continue;
		const width = boneWidth(bone);
		const length = Math.hypot(b.x - a.x, b.y - a.y);
		const tx = (b.x - a.x) / length;
		const ty = (b.y - a.y) / length;
		const dir = bone.ux * ty - bone.uy * tx >= 0 ? 1 : -1;
		const sx = bone.ux * dir;
		const sy = bone.uy * dir;
		const deep = {x: hit.x - sx * rim * 0.75, y: hit.y - sy * rim * 0.75};
		const stubSide = mulberry32(Math.round(hit.x * 7 + hit.y * 131))() < 0.5 ? -1 : 1;
		if (cell.sides[a.cut] === stubSide) {
			const reach = width * 0.6 + 3 + random() * 5;
			const tip = {x: hit.x + sx * reach, y: hit.y + sy * reach};
			shapes.bone.push({type: 'poly', points: brokenBone(deep, tip, width, random), fill: PALETTE.bone, stroke: PALETTE.boneLine, strokeWidth: 0.9});
			shapes.bone.push({type: 'ellipse', x: tip.x - sx * 1.6, y: tip.y - sy * 1.6, rx: width * 0.18, ry: width * 0.12, rotation: Math.atan2(sy, sx), fill: PALETTE.marrow});
			const splinters = 1 + Math.floor(random() * 2);
			for (let k = 0; k < splinters; k++) {
				const side = k === 0 ? 1 : -1;
				const root = {x: hit.x + sx * reach * 0.35 - sy * side * width * 0.45, y: hit.y + sy * reach * 0.35 + sx * side * width * 0.45};
				const spike = {x: root.x + sx * (2 + random() * 3) - sy * side * (0.8 + random() * 1.4), y: root.y + sy * (2 + random() * 3) + sx * side * (0.8 + random() * 1.4)};
				shapes.bone.push({type: 'poly', points: [
					{x: root.x - sx * 1.5 + sy * side * 0.2, y: root.y - sy * 1.5 - sx * side * 0.2},
					{x: root.x + sy * side * 1.1, y: root.y - sx * side * 1.1},
					spike,
				], fill: PALETTE.bone, stroke: PALETTE.boneLine, strokeWidth: 0.7});
			}
		} else {
			const end = {x: hit.x + sx * (0.5 + random()), y: hit.y + sy * (0.5 + random())};
			shapes.bone.push({type: 'poly', points: brokenBone(deep, end, width, random), fill: PALETTE.bone, stroke: PALETTE.boneLine, strokeWidth: 0.9});
			shapes.bone.push({type: 'ellipse', x: end.x - sx * 1.2, y: end.y - sy * 1.2, rx: width * 0.2, ry: width * 0.13, rotation: Math.atan2(sy, sx), fill: PALETTE.marrow});
		}
	}
};

// Lines through a point near the middle of `box`, numbered from `first`. What is
// left of a cut part tears across its longer side.
const tearLines = (box, count, random, bone, first, across) => {
	const impact = {x: box.x + box.w * (0.4 + random() * 0.2), y: box.y + box.h * (0.4 + random() * 0.2)};
	const base = across ? (box.h > box.w ? 0 : Math.PI / 2) : bone ? Math.atan2(bone.uy, bone.ux) + Math.PI / 2 : random() * Math.PI;
	const lines = [];
	for (let i = 0; i < count; i++) {
		const angle = base + i * Math.PI / count + (random() * 2 - 1) * (bone ? 0.45 : 0.3);
		const px = impact.x + (random() * 2 - 1) * box.w * 0.12;
		const py = impact.y + (random() * 2 - 1) * box.h * 0.12;
		lines.push({id: first + i, px, py, nx: -Math.sin(angle), ny: Math.cos(angle)});
	}
	return lines;
};

const boxOf = poly => {
	const xs = poly.map(v => v.x);
	const ys = poly.map(v => v.y);
	const x = Math.min(...xs);
	const y = Math.min(...ys);
	return {x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y};
};

// `start` tears what is left of a part that was cut before; `cuts` are those cuts,
// which get a narrow rim.
export const tearPieces = (art, {lines: count, inside, bone, blood = PALETTE.meatBright, seed, start, cuts = []}) => {
	const random = mulberry32(seed);
	const box = start ? boxOf(start.poly) : {x: 0, y: 0, w: art.w, h: art.h};
	const lines = tearLines(box, count, random, bone, cuts.reduce((next, cut) => Math.max(next, cut.id + 1), 0), !!start);
	const cells = splitCells([start || {poly: frameRect(art.w, art.h), sides: {}}], lines, 2);
	const edges = [...cuts, ...lines];
	const cutIds = new Set(cuts.map(cut => cut.id));
	const pieces = [];
	for (const cell of cells) {
		const covered = coverage(art, cell.poly);
		if (covered < MIN_COVERAGE) continue;
		const rim = Math.max(TEAR + 1.5, Math.min(RIM * (0.85 + random() * 0.35), Math.sqrt(covered) * 0.32));
		const wave = Math.min(WAVE, rim - TEAR - 0.6);
		const outer = raggedPolygon(cell.poly, () => TEAR, TEAR_SPACING);
		let inner = insetCell(cell, edges, id => cutIds.has(id) ? CUT_RIM : rim);
		if (inner) inner = raggedPolygon(inner, id => cutIds.has(id) ? CUT_RIM - TEAR - 0.6 : wave, WAVE_SPACING, 7);
		const shapes = {band: [], bandTop: [], stains: [], lumps: [], edges: [], speckles: [], bone: [], fibres: [], fibresTop: []};
		const outerRuns = [];
		for (const line of cutPolylines(outer)) outerRuns.push(...artRuns(line.points, art.alphaAt));
		const innerRuns = [];
		if (inner) for (const line of cutPolylines(inner)) innerRuns.push(...artRuns(line.points, art.alphaAt));
		for (const run of innerRuns) {
			if (inside === 'skull') skullEdge(shapes, run);
			else shapes.bandTop.push({type: 'line', points: run, width: 2.2, color: blood});
			stains(shapes, art, run, random, blood, 1, 2.8);
			speckles(shapes, art, inner, run, random, blood, 0.8);
		}
		for (const run of outerRuns) {
			blotches(shapes, run, random, rim * 0.85, 1, inside === 'skull');
			lumps(shapes, run, random);
			if (inside === 'ribs') ribs(shapes, art, run, random, rim, false);
			if (inside === 'guts') guts(shapes, run, random, rim);
			shapes.edges.push({type: 'line', points: run, width: 0.8, color: PALETTE.meatDark});
			fibres(shapes, run, random);
		}
		if (bone) limbBone(shapes, art, bone, cell, random, rim);
		pieces.push({
			outer,
			inner,
			coverage: covered,
			under: shapes.band,
			over: [...shapes.bandTop, ...shapes.stains],
			overlay: [...shapes.speckles, ...shapes.edges, ...shapes.lumps, ...shapes.fibres, ...shapes.fibresTop, ...shapes.bone],
		});
	}
	return pieces.sort((a, b) => b.coverage - a.coverage);
};
